// =========================================================================
// Edge Preview (W4)
// =========================================================================
// spec/closing_river_web_mvp_v2.md
//   §「Engine Adapter preview/operate contract」
//   §「線質差カタログ」 active 辺 (タップ可能ハイライト) + プレビュー値表示
//   §「画面フロー」 辺タップ → Preview Highlight + Preview Panel
//
// 役割:
//   1) engine の previewEdges(state) からタップ可能な辺ごとの UI 表示用
//      プレビュー値 (srcAfter / dstAfter / isMerge) を計算する。
//   2) 画面下部固定の preview-bar を描画する pure UI 関数を提供する。
//      controller を呼ばない (controller が onConfirm を持つだけ)。
//
// 注意:
//   - engine の EdgePreview.resultPreview は subtract なら abs(u-v)、
//     merge なら u.value (= v.value) を返すだけで、どちらが「消える / 変わる」 か
//     の情報を持たない。UI では「7 → 4 (3 は変化なし)」 を表示するため、
//     subtract 時に大→小の方向を判別して srcAfter/dstAfter を算出する。
//     (operation.ts §2: 大きい側が abs(u-v) に置き換わる、小さい側は変化なし)
// =========================================================================

import { previewEdges } from '../engine-adapter.js';
import type { EdgePreview as EngineEdgePreview, GameState } from '../engine-adapter.js';

// -------------------------------------------------------------------------
// UI 向けプレビュー型
// -------------------------------------------------------------------------

/**
 * W4 UI の preview-bar / confirm-modal が使う表示用の型。
 * engine の EdgePreview と別物 (UI 寄りに `srcAfter`/`dstAfter`/`isMerge` を持つ)。
 *
 * spec §「画面フロー」: 「7 → 4, 辺消費」 (= subtract) / 「合流」 (= merge)
 */
export interface EdgePreview {
  readonly edgeId: number;
  readonly srcValue: number;
  readonly dstValue: number;
  /** 操作後の src 側の値。merge では src ノードが消えるので mergedValue を入れる */
  readonly srcAfter: number;
  /** 操作後の dst 側の値。merge では dst ノードが消えるので mergedValue を入れる */
  readonly dstAfter: number;
  /** value(u) === value(v) のとき true (= 合流) */
  readonly isMerge: boolean;
  /** 必ず true (subtract も merge も操作辺 e は consumed として 1 本消費) */
  readonly consumesEdge: true;
}

// -------------------------------------------------------------------------
// 計算
// -------------------------------------------------------------------------

/**
 * engine の previewEdges(state) を UI 向け EdgePreview へ変換して
 * edgeId → EdgePreview の Map を返す。
 *
 * controller / canvas / edge-interaction が edgeId を引いて UI 表示するための
 * 共通 hub。Map の挿入順は engine 側の edgeId 昇順 (= 決定論的)。
 */
export function buildEdgePreviewMap(state: GameState): Map<number, EdgePreview> {
  const map = new Map<number, EdgePreview>();
  const engineList = previewEdges(state);
  for (const ep of engineList) {
    map.set(ep.edgeId, toUiPreview(ep));
  }
  return map;
}

/** engine の EdgePreview を UI 表示型に変換 (純関数、test 用に export) */
export function toUiPreview(ep: EngineEdgePreview): EdgePreview {
  if (ep.operationKind === 'merge') {
    // §3 合流: 両方が削除されて新ノードに統合される。mergedValue = u.value (= v.value)
    return {
      edgeId: ep.edgeId,
      srcValue: ep.srcValue,
      dstValue: ep.dstValue,
      srcAfter: ep.resultPreview,
      dstAfter: ep.resultPreview,
      isMerge: true,
      consumesEdge: true,
    };
  }
  // §2 減算: 大きい側が |u-v| に、小さい側は変化なし
  const after = ep.resultPreview;
  let srcAfter: number;
  let dstAfter: number;
  if (ep.srcValue > ep.dstValue) {
    srcAfter = after;
    dstAfter = ep.dstValue;
  } else if (ep.dstValue > ep.srcValue) {
    srcAfter = ep.srcValue;
    dstAfter = after;
  } else {
    // srcValue === dstValue かつ subtract は engine 仕様上ありえない (merge になる)
    // 保険として両方の値を after とする
    srcAfter = after;
    dstAfter = after;
  }
  return {
    edgeId: ep.edgeId,
    srcValue: ep.srcValue,
    dstValue: ep.dstValue,
    srcAfter,
    dstAfter,
    isMerge: false,
    consumesEdge: true,
  };
}

// -------------------------------------------------------------------------
// 表示文字列ヘルパ (test 容易、confirm-modal でも再利用)
// -------------------------------------------------------------------------

/** 値変化の主表示。 例: "7 → 4"、合流時: "3 + 3 → 3" */
export function formatPreviewValues(preview: EdgePreview): string {
  if (preview.isMerge) {
    return `${preview.srcValue} + ${preview.dstValue} → ${preview.srcAfter}`;
  }
  // subtract: 大きい方を主役にする (= 値が変わる側)
  if (preview.srcValue > preview.dstValue) {
    return `${preview.srcValue} → ${preview.srcAfter}`;
  }
  return `${preview.dstValue} → ${preview.dstAfter}`;
}

/** 副表示 (操作の意味)。 "辺消費" / "合流 → 1 ノード減" */
export function formatPreviewLabel(preview: EdgePreview): string {
  return preview.isMerge ? '合流 → 1 ノード減' : '辺消費';
}

// -------------------------------------------------------------------------
// preview-bar 描画 (pure UI、controller を呼ばない)
// -------------------------------------------------------------------------

const BAR_CLASS = 'cr-preview-bar';
const BAR_VALUE_CLASS = 'cr-preview-bar__value';
const BAR_LABEL_CLASS = 'cr-preview-bar__label';
const BAR_EMPTY_CLASS = 'cr-preview-bar__empty';

let stylesInjected = false;

/**
 * preview-bar スタイルを document head に <style> として注入する (1度だけ)。
 * styles.css (W1) を編集できないため、W4 自身で持つ。
 * tokens (--bg-elev/--text/--text-dim/--accent 等) は styles.css の :root を継承。
 */
function ensureStyles(): void {
  if (stylesInjected) return;
  if (typeof document === 'undefined') return;
  if (document.getElementById('cr-w4-styles')) {
    stylesInjected = true;
    return;
  }
  const style = document.createElement('style');
  style.id = 'cr-w4-styles';
  style.textContent = W4_CSS;
  document.head.appendChild(style);
  stylesInjected = true;
}

/**
 * 画面下部固定の preview-bar を container 内に描画 / 更新する。
 * - container は最初の呼び出しで一度だけ root 要素を生成する
 * - preview === null のときは「辺を選んでください」 を薄く表示
 *
 * 呼び出し側 (controller) が選択 edge が切り替わるたびに呼ぶ想定。
 * このファイルが controller を呼ぶことはない。
 */
export function renderPreviewBar(
  container: HTMLElement,
  preview: EdgePreview | null,
): void {
  ensureStyles();
  let bar = container.querySelector<HTMLDivElement>(`.${BAR_CLASS}`);
  if (!bar) {
    bar = document.createElement('div');
    bar.className = BAR_CLASS;
    bar.setAttribute('role', 'status');
    bar.setAttribute('aria-live', 'polite');
    container.appendChild(bar);
  }
  if (preview === null) {
    bar.dataset.state = 'empty';
    bar.innerHTML = '';
    const empty = document.createElement('span');
    empty.className = BAR_EMPTY_CLASS;
    empty.textContent = '辺を選んでください';
    bar.appendChild(empty);
    return;
  }
  bar.dataset.state = preview.isMerge ? 'merge' : 'subtract';
  bar.innerHTML = '';

  const value = document.createElement('span');
  value.className = BAR_VALUE_CLASS;
  value.textContent = formatPreviewValues(preview);

  const label = document.createElement('span');
  label.className = BAR_LABEL_CLASS;
  label.textContent = formatPreviewLabel(preview);

  bar.appendChild(value);
  bar.appendChild(label);
}

// -------------------------------------------------------------------------
// W4 専用 CSS (preview-bar / confirm-modal / turn-indicator)
// -------------------------------------------------------------------------
// confirm-modal.ts / turn-indicator.ts も同じスタイル sheet を共有するため
// ここで一括定義し、各ファイルが renderPreviewBar 経由 (= ensureStyles) で
// 注入されることを期待する。
// confirm-modal / turn-indicator も内部で ensureStyles を呼ぶ。
// -------------------------------------------------------------------------

const W4_CSS = `
/* ===== preview-bar ===== */
.${BAR_CLASS} {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px calc(12px + env(safe-area-inset-bottom)) 20px;
  background: var(--bg-elev, #131922);
  border-top: 1px solid var(--border, #2a3445);
  color: var(--text, #d6dde6);
  font-size: var(--font-base, 15px);
  min-height: 56px;
  transition: background var(--t-fast, 150ms) ease;
}
.${BAR_CLASS}[data-state="merge"] {
  background: linear-gradient(
    to top,
    color-mix(in srgb, var(--accent, #4dd0c8) 8%, var(--bg-elev, #131922)),
    var(--bg-elev, #131922)
  );
}
.${BAR_VALUE_CLASS} {
  font-size: var(--font-xl, 22px);
  font-weight: 500;
  letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;
  color: var(--text, #d6dde6);
}
.${BAR_LABEL_CLASS} {
  font-size: var(--font-sm, 13px);
  color: var(--text-dim, #7c8694);
}
.${BAR_EMPTY_CLASS} {
  font-size: var(--font-sm, 13px);
  color: var(--text-dim, #7c8694);
  opacity: 0.7;
}

/* ===== confirm-modal ===== */
.cr-confirm-overlay {
  position: fixed;
  inset: 0;
  z-index: 40;
  background: rgba(10, 14, 20, 0.66);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  opacity: 0;
  transition: opacity var(--t-fast, 150ms) ease;
}
.cr-confirm-overlay[data-state="open"] {
  opacity: 1;
}
.cr-confirm-card {
  background: var(--bg-elev, #131922);
  border: 1px solid var(--border, #2a3445);
  border-radius: var(--radius-lg, 16px);
  padding: 24px 20px 16px 20px;
  width: 100%;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transform: translateY(8px);
  transition: transform var(--t-fast, 150ms) ease;
}
.cr-confirm-overlay[data-state="open"] .cr-confirm-card {
  transform: translateY(0);
}
.cr-confirm-card__title {
  font-size: var(--font-sm, 13px);
  color: var(--text-dim, #7c8694);
  letter-spacing: 0.04em;
}
.cr-confirm-card__value {
  font-size: 32px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: var(--text, #d6dde6);
  text-align: center;
  padding: 4px 0;
}
.cr-confirm-card__label {
  font-size: var(--font-base, 15px);
  color: var(--text-dim, #7c8694);
  text-align: center;
}
.cr-confirm-card__hint {
  font-size: var(--font-sm, 13px);
  color: var(--text-dim, #7c8694);
  text-align: center;
  opacity: 0.7;
}
.cr-confirm-card__buttons {
  display: flex;
  gap: 12px;
  margin-top: 4px;
}
.cr-confirm-card__buttons button {
  flex: 1;
  padding: 14px 16px;
  font-size: var(--font-base, 15px);
  border-radius: var(--radius-md, 10px);
  cursor: pointer;
  transition: transform var(--t-fast, 150ms) ease,
    background var(--t-fast, 150ms) ease,
    border-color var(--t-fast, 150ms) ease;
}
.cr-confirm-card__buttons button:active {
  transform: scale(0.97);
}
.cr-confirm-primary {
  background: var(--accent, #4dd0c8);
  color: #0a0e14;
  border: 1px solid var(--accent, #4dd0c8);
  font-weight: 500;
}
.cr-confirm-primary:hover {
  background: var(--accent-strong, #7fe6df);
  border-color: var(--accent-strong, #7fe6df);
}
.cr-confirm-ghost {
  background: transparent;
  color: var(--text, #d6dde6);
  border: 1px solid var(--border, #2a3445);
}
.cr-confirm-ghost:hover {
  background: var(--bg-elev-2, #1c2532);
}

/* ===== turn-indicator ===== */
.cr-turn-indicator {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 6px 12px;
  border-radius: var(--radius-md, 10px);
  background: var(--bg-elev, #131922);
  border: 1px solid var(--border, #2a3445);
  font-variant-numeric: tabular-nums;
  transition: border-color var(--t-fast, 150ms) ease,
    color var(--t-fast, 150ms) ease;
}
.cr-turn-indicator__used {
  font-size: var(--font-lg, 18px);
  font-weight: 500;
  color: var(--text, #d6dde6);
}
.cr-turn-indicator__sep,
.cr-turn-indicator__limit {
  font-size: var(--font-sm, 13px);
  color: var(--text-dim, #7c8694);
}
.cr-turn-indicator[data-warn="true"] {
  border-color: var(--warn-soft, #c9a46f);
}
.cr-turn-indicator[data-warn="true"] .cr-turn-indicator__used {
  color: var(--warn-soft, #c9a46f);
}
`;

// 他ファイル (confirm-modal / turn-indicator) からも呼べるよう公開
export const __ensureW4Styles = ensureStyles;
